import posthog from 'posthog-js'
import { type EventName, type CommonProps, type UserProps } from './constants'
import {
	FIRST_TOUCH_COOKIE,
	FIRST_TOUCH_MAX_AGE_SECONDS,
	firstTouchFromUrl,
	serializeFirstTouch,
} from './first-touch'

/**
 * Client-side analytics helper
 *
 * Why wrap PostHog?
 * - Consistent API across the app
 * - Built-in error handling
 * - Automatic property injection
 * - Easy to mock in tests
 */

let isInitialized = false

/**
 * Events captured before PostHog finished initializing, replayed on init. See
 * `trackClient` for why this exists rather than dropping them.
 */
type PendingEvent = {
	eventName: EventName
	properties?: CommonProps & Record<string, unknown>
	insertId?: string
}
const pendingEvents: PendingEvent[] = []
const MAX_PENDING_EVENTS = 50

/**
 * localStorage marker recording that PostHog currently holds an IDENTIFIED
 * distinct id. Survives a full page load, which a React ref does not, so a
 * logout that reloads the document is still recognised as a real
 * authenticated→anonymous transition.
 */
const IDENTIFIED_MARKER = 'ph_identified_user'

/**
 * Write first-touch ad attribution to a first-party cookie, FIRST touch wins:
 * an existing cookie is never overwritten, so a later organic visit can't
 * relabel a lead that originally came from an ad.
 *
 * Lax + non-Secure-on-localhost so it survives the checker's POST→redirect.
 */
function persistFirstTouch(url: URL) {
	try {
		if (document.cookie.includes(`${FIRST_TOUCH_COOKIE}=`)) return
		const touch = firstTouchFromUrl(url, document.referrer)
		if (!touch) return

		const secure = window.location.protocol === 'https:' ? '; Secure' : ''
		document.cookie =
			`${FIRST_TOUCH_COOKIE}=${serializeFirstTouch(touch)}` +
			`; Max-Age=${FIRST_TOUCH_MAX_AGE_SECONDS}; Path=/; SameSite=Lax${secure}`
	} catch (error) {
		console.error('Failed to persist first-touch attribution:', error)
	}
}

export function initAnalyticsClient() {
	// Only initialize once
	if (isInitialized) return

	// Ad attribution is persisted to OUR cookie and OUR tables, so it must not
	// depend on PostHog being configured. Do it before the key check.
	persistFirstTouch(new URL(window.location.href))

	// Environment-based initialization
	const posthogKey = ENV.POSTHOG_KEY

	if (!posthogKey) {
		console.log('PostHog key not configured for environment:', ENV.MODE)
		return
	}

	// Skip initialization on localhost in development
	// if (ENV.MODE === 'development' && window.location.hostname === 'localhost') {
	// 	console.log('PostHog disabled on localhost')
	// 	return
	// }

	try {
		posthog.init(posthogKey, {
			api_host: ENV.POSTHOG_HOST ?? 'https://us.i.posthog.com',
			capture_pageview: false, // We handle pageviews manually for better control
			capture_exceptions: true,
			autocapture: true, // Captures clicks, form submissions automatically
			debug: ENV.MODE === 'development',
			// Respect user privacy
			respect_dnt: true,
			opt_out_capturing_by_default: false,
		})

		// Set global properties that appear on every event
		posthog.register({
			environment: ENV.MODE,
			app_version: ENV.APP_VERSION,
		})

		// Capture first-touch attribution (only set once per user)
		const url = new URL(window.location.href)
		const utmParams = {
			initial_utm_source: url.searchParams.get('utm_source'),
			initial_utm_medium: url.searchParams.get('utm_medium'),
			initial_utm_campaign: url.searchParams.get('utm_campaign'),
			initial_utm_term: url.searchParams.get('utm_term'),
			initial_utm_content: url.searchParams.get('utm_content'),
			initial_gclid: url.searchParams.get('gclid'),
			initial_referrer: document.referrer || undefined,
			initial_landing_page: window.location.pathname,
		}

		// Only set if values exist (register_once won't override existing values)
		const validUtmParams = Object.fromEntries(
			Object.entries(utmParams).filter(([_, value]) => value),
		)
		if (Object.keys(validUtmParams).length > 0) {
			posthog.register_once(validUtmParams)
		}

		// Make available globally for debugging
		if (ENV.MODE === 'development') {
			;(window as any).posthog = posthog
		}

		isInitialized = true

		// Replay anything captured while we were still booting, in order.
		const buffered = pendingEvents.splice(0, pendingEvents.length)
		for (const event of buffered) {
			trackClient(event.eventName, event.properties, event.insertId)
		}
	} catch (error) {
		console.error('Failed to initialize PostHog:', error)
	}
}

/**
 * Identify a user with PostHog
 * Call this when user logs in or signs up
 */
export function identifyClient(userId: string, properties?: UserProps) {
	if (!isInitialized) return

	try {
		posthog.identify(userId, properties)
		localStorage.setItem(IDENTIFIED_MARKER, userId)
	} catch (error) {
		console.error('Failed to identify user:', error)
	}
}

/**
 * Reset user identity. Call this when a user logs out.
 *
 * NEVER call this simply because the current request is anonymous.
 * `posthog.reset()` rotates the anonymous distinct id, so resetting on every
 * anonymous page load would split one visitor's journey across several
 * identities — the free funnel is entirely anonymous and spans full page loads
 * (the emailed report link is a cold load), so scan events and the later
 * report/checkout events would never join up.
 *
 * The marker is what makes this precise: it is set only by `identifyClient`, so
 * a reset happens on a genuine authenticated→anonymous transition and nowhere
 * else. It lives in localStorage rather than a React ref so a logout that
 * reloads the document is still recognised.
 */
export function resetClient() {
	if (!isInitialized) return

	try {
		if (!localStorage.getItem(IDENTIFIED_MARKER)) return
		localStorage.removeItem(IDENTIFIED_MARKER)
		posthog.reset()
	} catch (error) {
		console.error('Failed to reset PostHog:', error)
	}
}

/**
 * Track an event with consistent properties and deduplication.
 *
 * Events captured BEFORE init are buffered, not dropped. `<Outlet />` renders
 * above `<PostHogClient />` in the root, so a route's mount effect runs before
 * `initAnalyticsClient()` on any full page load — exactly what happens when
 * someone opens an emailed report link. Dropping there would silently lose the
 * first event of every cold-loaded page, and callers that mark themselves "done"
 * (useTrackOnce) would never retry.
 */
export function trackClient(
	eventName: EventName,
	properties?: CommonProps & Record<string, unknown>,
	insertId?: string, // For deduplication
) {
	if (!isInitialized) {
		// Bounded: if PostHog is never configured, this must not grow forever.
		if (pendingEvents.length < MAX_PENDING_EVENTS) {
			pendingEvents.push({ eventName, properties, insertId })
		}
		return
	}

	try {
		// Add current UTM parameters if they exist in URL (for engagement campaigns)
		const url = new URL(window.location.href)
		const currentUtms = {
			current_utm_source: url.searchParams.get('utm_source'),
			current_utm_medium: url.searchParams.get('utm_medium'),
			current_utm_campaign: url.searchParams.get('utm_campaign'),
			current_utm_term: url.searchParams.get('utm_term'),
			current_utm_content: url.searchParams.get('utm_content'),
			current_gclid: url.searchParams.get('gclid'),
		}

		const validCurrentUtms = Object.fromEntries(
			Object.entries(currentUtms).filter(([_, value]) => value),
		)

		const eventProperties = {
			...validCurrentUtms,
			...properties,
			...(insertId && { $insert_id: insertId }), // Consistent deduplication
		}

		posthog.capture(eventName, eventProperties)
	} catch (error) {
		console.error('Failed to track event:', error)
	}
}

/**
 * Get the current PostHog distinct ID
 * Useful for passing to server-side events
 */
export function getDistinctId(): string | undefined {
	if (!isInitialized) return undefined

	try {
		return posthog.get_distinct_id()
	} catch (error) {
		console.error('Failed to get distinct ID:', error)
		return undefined
	}
}

/**
 * Update user properties without triggering an event
 */
export function setUserProperties(properties: UserProps) {
	if (!isInitialized) return

	try {
		posthog.people.set(properties)
	} catch (error) {
		console.error('Failed to set user properties:', error)
	}
}
